/**
 * Shared Monero address helpers.
 *
 * Single source of truth for Monero address validation + the XMR asset
 * constants used across the Privacy Suite (Monero Send Wizard) and the
 * Exchange (cross-chain swaps to XMR via THORChain). Kept dependency-free
 * so it can be imported from both .jsx and .ts without pulling in config
 * or network code.
 */

// Native Monero precision (atomic units = piconero, 1 XMR = 1e12).
export const MONERO_DECIMALS = 12;

// SwapKit-style chain + asset identifiers for Monero.
export const XMR_CHAIN = 'XMR';
export const XMR_IDENTIFIER = 'XMR.XMR';

/**
 * Structural validator for Monero addresses.
 *
 * Mainnet primary addresses start with `4` (95 base58 chars), integrated
 * addresses also start with `4` (106 chars), and subaddresses start with
 * `8` (95 chars). This is a shape check (leading byte + base58 alphabet +
 * length), not a full keccak checksum decode — it mirrors the long-standing
 * check used by the Monero Send Wizard recipient step.
 */
export const MONERO_ADDRESS_REGEX = /^[48][0-9A-HJ-NP-Za-km-z]{94,105}$/;

/**
 * @param {string} address
 * @returns {boolean} true when the string is shaped like a Monero address.
 */
export const isValidMoneroAddress = (address) => {
	if (typeof address !== 'string') return false;
	return MONERO_ADDRESS_REGEX.test(address.trim());
};

/**
 * Classify a (valid-looking) Monero address.
 * @param {string} address
 * @returns {('primary'|'integrated'|'subaddress'|null)}
 */
export const classifyMoneroAddress = (address) => {
	if (!isValidMoneroAddress(address)) return null;
	const trimmed = address.trim();
	if (trimmed.startsWith('8')) return 'subaddress';
	// Integrated addresses embed an 8-byte payment id → ~106 chars.
	if (trimmed.length >= 104) return 'integrated';
	return 'primary';
};

/**
 * @param {string} identifier asset identifier such as "XMR.XMR".
 * @returns {boolean}
 */
export const isXmrIdentifier = (identifier) =>
	typeof identifier === 'string' && identifier.toUpperCase().split('-')[0] === XMR_IDENTIFIER;

/**
 * @param {{identifier?: string, chain?: string}|null|undefined} token
 * @returns {boolean}
 */
export const isXmrToken = (token) => {
	if (!token) return false;
	if (isXmrIdentifier(token.identifier)) return true;
	return typeof token.chain === 'string' && token.chain.toUpperCase() === XMR_CHAIN;
};
